// SNOWCLUB-LAUNCH-REMAINING-PARITY-001 — "did the SERVER author this document's head?"
//
// The one predicate behind `window.__SG_SERVER_HEAD` (see @/components/site/DesignSurface.tsx, which
// sets the flag before the design export evaluates). The export's `syncHead()` writes
// title/description/canonical/og/twitter from its own SEO map on mount; the head-authority patch
// makes it return early when this build already said what the head should be. Extracted here so the
// condition can be stated once and tested directly — it was previously an inline expression inside a
// mount effect, which is why it went two commits without anyone noticing which routes it missed.
//
// THE RULE: a server that said EITHER of these two things has authored the head —
//   · WHERE the page lives          `<link rel="canonical">`   (generateMetadata's `alternates`)
//   · WHETHER it should be indexed  `<meta name="robots">`     (generateMetadata's `robots`)
//
// The second half is the correction. Checking only the canonical meant a DELIBERATELY NOINDEXED
// route — every `matchBookingFunnel` path returns `{ title, robots: { index: false, follow: true } }`
// and no `alternates` — read as "the server said nothing", so the design dropped the noindex and
// wrote a canonical built from `location.origin`. Measured on the served candidate: fetched
// `noindex, follow` with no canonical; rendered, no noindex and
// `canonical http://localhost:3025/mountain-whistler/bus-schedule`. On staging that same expression
// yields a `stg.mysnowclub.com` canonical, which is the opposite of this build's measured contract
// that staging serves the PRODUCTION canonical.
//
// The surfaces that have no server head at all keep the old behaviour by construction: `/flights`
// and `/stays` serve neither tag (re-measured), so this returns false there and `syncHead()` remains
// the only thing that sets their head, including the noindex it sets.
export function serverAuthoredHead(doc: Pick<Document, "querySelector">): boolean {
  return !!doc.querySelector('link[rel="canonical"]') || !!doc.querySelector('meta[name="robots"]');
}
